import React, { useState, useCallback, useMemo } from "react";
import { CopyIcon } from "../../icons";
import CryptoBottomSheet from "../CryptoBottomSheet";
import BeneficiaryAvatar from "./BeneficiaryAvatar";
import DetailRow from "./DetailRow";
import NewCommonButton from "../common/NewCommonButton";

const truncateAddress = (address = "") => {
  if (address.length <= 16) return address;
  return `${address.slice(0, 7)}...${address.slice(-5)}`;
};

const WalletAddressValue = ({ address, copied, onCopy }) => (
  <button
    type="button"
    onClick={onCopy}
    className="flex items-center gap-2 rounded bg-background-alt px-1.5 py-0.5 custom-transition hover:opacity-80"
    aria-label="Copy wallet address"
  >
    <span className="whitespace-nowrap text-sm font-medium leading-[16.5px] text-text-primary">
      {truncateAddress(address)}
    </span>
    <CopyIcon className="shrink-0 text-text-secondary" />
    {copied && (
      <span className="text-[10px] font-medium text-[#4ab932]">Copied</span>
    )}
  </button>
);

const BeneficiaryDetailsFields = ({ beneficiary, displayName, copied, onCopy }) => {
  return (
    <div className="overflow-hidden rounded-[8px] border border-divider bg-background-alt">
      <DetailRow
        label="Beneficiary Name"
        value={displayName}
        showInfo
      />
      <DetailRow
        label="Beneficiary Type"
        value={beneficiary?.beneficiary_type || "INDIVIDUAL"}
        showInfo
      />
      <DetailRow
        label="Wallet Type"
        value={beneficiary?.wallet_type || "CUSTODIAL"}
      />
      <DetailRow
        label="Wallet Address"
        showInfo
        valueContent={
          <WalletAddressValue
            address={beneficiary?.wallet_address}
            copied={copied}
            onCopy={onCopy}
          />
        }
      />
      {beneficiary?.asset_id && (
        <DetailRow
          label="Asset"
          value={beneficiary.asset_id}
          showInfo
        />
      )}
      {(beneficiary?.status) && (
        <DetailRow
          label="Status"
          value={beneficiary?.status}
        />
      )}
      {beneficiary?.beneficiary_vasp_name && (
        <DetailRow
          label="Exchange (VASP)"
          value={beneficiary.beneficiary_vasp_name}
          showInfo
        />
      )}
    </div>
  );
};

const BeneficiaryDetailsContent = ({
  beneficiary,
  displayName,
  onTransfer,
  copied,
  onCopy,
  isMobile = false,
}) => {
  return (
    <div className="flex flex-col gap-4">
      {isMobile && (
        <h2 className="text-xl font-semibold leading-7 text-text-primary">
          Beneficiary Details
        </h2>
      )}

      {/* Avatar Header */}
      <div className="border-b border-divider pb-4 pt-1">
        <div className="flex items-center gap-3">
          <BeneficiaryAvatar name={displayName} />
          <div className="flex flex-col min-w-0">
            <p className="text-sm font-semibold leading-normal text-text-primary truncate">
              {displayName}
            </p>
            {beneficiary?.wallet_address && (
              <span className="text-xs text-text-secondary truncate">
                {truncateAddress(beneficiary.wallet_address)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Field List & Transfer CTA */}
      <div className="flex flex-col gap-6">
        <BeneficiaryDetailsFields
          beneficiary={beneficiary}
          displayName={displayName}
          copied={copied}
          onCopy={onCopy}
        />

        <NewCommonButton
          type="button"
          onClick={onTransfer}
          disabled={String(beneficiary?.status).toLocaleLowerCase() !== "approved"}
          className="flex h-10 w-full items-center justify-center rounded-[8px] bg-[#4ab933] text-sm font-semibold leading-[21px] text-white custom-transition hover:bg-[#3fa82c]"
        >
          Transfer Amount
        </NewCommonButton>
      </div>
    </div>
  );
};

const BeneficiaryDetailsPanel = ({
  beneficiary,
  onTransfer,
  onClose,
  isOpen = false,
}) => {
  const [copied, setCopied] = useState(false);

  // Parse beneficiary name safely
  const displayName = useMemo(() => {
    if (!beneficiary) return "";
    const fullName = [beneficiary.beneficiary_first_name, beneficiary.beneficiary_last_name]
      .filter(Boolean)
      .join(" ")
      .trim();

    if (fullName) return `${fullName} (${beneficiary.asset_id?.split("_")[0] || "Crypto"})`;
    if (beneficiary.beneficiary_legal_name) return beneficiary.beneficiary_legal_name;
    return beneficiary.label || "Beneficiary";
  }, [beneficiary]);

  const handleCopy = useCallback(async () => {
    if (!beneficiary?.wallet_address) return;
    try {
      await navigator.clipboard.writeText(beneficiary.wallet_address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [beneficiary?.wallet_address]);

  if (!beneficiary) {
    return (
      <div className="md:flex w-full max-w-[423px] flex-1 flex-col gap-4 hidden">
        <p className="text-xl font-semibold leading-7 text-text-primary">Details</p>
        <p className="text-sm text-text-secondary">Select a beneficiary to view details.</p>
      </div>
    );
  }

  return (
    <>
      {/* Desktop Panel View */}
      <div className="hidden w-full max-w-[423px] flex-1 flex-col gap-4 md:flex">
        <p className="text-xl font-semibold leading-7 text-text-primary">Details</p>

        <BeneficiaryDetailsContent
          beneficiary={beneficiary}
          displayName={displayName}
          onTransfer={onTransfer}
          copied={copied}
          onCopy={handleCopy}
        />
      </div>

      {/* Mobile Bottom Sheet View */}
      <div className="block md:hidden">
        <CryptoBottomSheet
          isOpen={isOpen}
          onClose={onClose}
          showHeader={false}
          panelClassName="!py-0"
          contentClassName="min-h-0 flex-1 overflow-y-auto hide-scrollbar px-6 pb-6 pt-6"
        >
          <BeneficiaryDetailsContent
            beneficiary={beneficiary}
            displayName={displayName}
            onTransfer={onTransfer}
            copied={copied}
            onCopy={handleCopy}
            isMobile
          />
        </CryptoBottomSheet>
      </div>
    </>
  );
};

export default React.memo(BeneficiaryDetailsPanel);